import { useCallback } from 'react'
import { useAbility, useGrantedPermissions } from './AbilityProvider'
import type { AppAbility } from './ability'

/**
 * Derived from the ability itself rather than hand-written, so the `asCard()` / `asMember()`
 * helpers — which return CASL's branded ForcedSubject type — are accepted without casts.
 */
type CanArgs = Parameters<AppAbility['can']>

/**
 * The permission hook.
 *
 * Conventions, which are in CLAUDE.md and enforced in review:
 *   - HIDE nav entries and whole pages the user cannot `read`.
 *   - DISABLE + tooltip for actions on things they can see but not change. Never a silent
 *     no-op — that generates support tickets.
 *   - READ-ONLY TEXT for form values, not a disabled input, which reads as broken.
 *   - Hiding is never security. The backend re-checks everything.
 */
export function usePermission() {
  const ability = useAbility()
  const granted = useGrantedPermissions()

  const can = useCallback(
    (...args: CanArgs) => ability.can(...args),
    [ability],
  )

  const cannot = useCallback(
    (...args: CanArgs) => ability.cannot(...args),
    [ability],
  )

  /** The reason a rule denied something, when the backend supplied one. */
  const reasonFor = useCallback(
    (...args: CanArgs) => ability.relevantRuleFor(...args)?.reason,
    [ability],
  )

  /**
   * Is this exact permission KEY granted?
   *
   * `can()` first, always — it carries the record conditions and is the whole reason the
   * ruleset is serialized. `has()` is the escape hatch for keys that share an (action,
   * subject) pair and so cannot be told apart from the packed rules: `view.share` and
   * `view.manageAny` are both `manage SavedView`. It reads the backend's own granted set,
   * so it is still the backend's answer — not a rule re-implemented here.
   */
  const has = useCallback((permission: string) => granted.has(permission), [granted])

  return { ability, can, cannot, reasonFor, has }
}
